'use client'

import { useState, useEffect } from 'react'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import ScrollView from '@/components/ui/ScrollView'
import { Placeholder } from '@/components/ui/Placeholder'
import AppIcon from '@/components/content/app/AppIcon'
import { getBrandColors } from '@/lib/utils'
import { queryPayload } from '@/lib/payloadClient'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import {
  Classification,
  Recommendation,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { createLogger } from '@/lib/logger'
import type { App, AppCategoryRecommendation, Category, Deal, Media } from '@/payload-types'

const logger = createLogger('BestCategories')

type BestCategoriesProps = {
  categories: Pick<Category, 'id' | 'name' | 'slug'>[]
  title: React.ReactNode
  // When provided (server-fetched on /best/:slug), the content renders from SSR data
  // with no client fetch or loading state. Falls back to the client fetch elsewhere.
  serverCategoriesWithApps?: CategoryWithApps[]
}

type CategoryWithApps = Pick<Category, 'id' | 'name' | 'slug'> & {
  apps: App[]
}

export default function BestCategories({
  categories,
  title,
  serverCategoriesWithApps,
}: BestCategoriesProps) {
  const [categoriesWithApps, setCategoriesWithApps] = useState<CategoryWithApps[]>(
    serverCategoriesWithApps ?? [],
  )
  const [isLoading, setIsLoading] = useState(!serverCategoriesWithApps)
  const [hasError, setHasError] = useState(false)
  const [retryToken, setRetryToken] = useState(0)

  useEffect(() => {
    // Server-provided on /best/:slug — content is already in the SSR HTML, skip the fetch.
    if (serverCategoriesWithApps) return

    if (!categories?.length) {
      setIsLoading(false)
      return
    }

    async function fetchAppRecommendations() {
      setIsLoading(true)
      setHasError(false)

      try {
        const categoryIds = categories.map((c) => c.id)

        // queryPayload (client-side) doesn't support dot-path where clauses
        // (e.g. `'app.review._status'`) the way payload.find() (server-side,
        // see fetchBestCategoriesWithApps) does — resolve the relationship to
        // IDs first instead, mirroring fetchAppOptions in PageSelect.tsx.
        const { docs: publishedReviews } = await queryPayload({
          collection: 'review',
          select: { app: true },
          where: { _status: { equals: 'published' } },
          depth: 0,
          pagination: false,
          cacheKey: 'query:review:published-app-ids',
        })
        const publishedAppIds = [
          ...new Set(
            publishedReviews
              .map((review) => (typeof review.app === 'number' ? review.app : review.app?.id))
              .filter((id): id is number => typeof id === 'number'),
          ),
        ]

        if (publishedAppIds.length === 0) {
          setCategoriesWithApps([])
          return
        }

        const { docs: appCategoryRecommendations } = await queryPayload({
          collection: 'app_category_recommendation',
          select: {
            app: true,
            category: true,
            classification: true,
            discontinuedAt: true,
            recommendation: true,
            order: true,
          },
          populate: {
            category: {},
            app: {
              slug: true,
              name: true,
              glyph: true,
              icon: true,
              brandPrimaryColor: true,
              brandBackgroundColor: true,
              deals: true,
            },
            deal: {},
            media: {
              alt: true,
              url: true,
              filename: true,
              coveragePercent: true,
              xOffsetPercent: true,
              yOffsetPercent: true,
              width: true,
              height: true,
            },
          },
          where: {
            and: [
              { category: { in: categoryIds } },
              { app: { in: publishedAppIds } },
              {
                or: [
                  {
                    classification: { equals: Classification.Primary },
                    discontinuedAt: { exists: false },
                  },
                  {
                    and: [
                      {
                        classification: { equals: Classification.Secondary },
                        discontinuedAt: { exists: false },
                      },
                      {
                        recommendation: {
                          in: [Recommendation.TopPick, Recommendation.Recommended],
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          sort: ['order'],
          depth: 2,
          pagination: false,
          cacheKey: `query:acr:best-cats:v2:${[...categoryIds].sort().join(',')}`,
        })

        // Group recommendations by category
        const categoryAppsMap = new Map<number, App[]>()
        const seenAppIdsByCategory = new Map<number, Set<App['id']>>()
        for (const rec of appCategoryRecommendations as AppCategoryRecommendation[]) {
          const categoryId = typeof rec.category === 'number' ? rec.category : rec.category?.id
          if (!categoryId || !rec.app) continue

          const app = rec.app as App
          if (!categoryAppsMap.has(categoryId)) {
            categoryAppsMap.set(categoryId, [])
            seenAppIdsByCategory.set(categoryId, new Set())
          }

          const seenAppIds = seenAppIdsByCategory.get(categoryId)!
          if (seenAppIds.has(app.id)) continue

          seenAppIds.add(app.id)
          categoryAppsMap.get(categoryId)!.push(app)
        }

        // Merge with original categories
        const categoriesWithApps: CategoryWithApps[] = categories
          .map((cat) => ({ ...cat, apps: categoryAppsMap.get(cat.id) ?? [] }))
          .filter((cat) => cat.apps.length > 0)

        setCategoriesWithApps(categoriesWithApps)
      } catch (error) {
        logger.error('Failed to load Best categories app recommendations', error)
        setCategoriesWithApps([])
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAppRecommendations()
  }, [categories, serverCategoriesWithApps, retryToken])

  if (!categories?.length) return null

  if (isLoading) {
    return (
      <div className="col-span-1 -ml-3 flex w-[calc(100%+24px)] flex-col gap-5 md:col-span-2 md:ml-0 md:w-full md:gap-12">
        <div className="px-3 md:px-0">{title}</div>
        <ScrollView className="flex gap-8 px-5 md:px-0" theme="dark">
          {categories.slice(0, 6).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-3">
              <Placeholder className="h-36 w-36 rounded-full" />
              <Placeholder className="h-5 w-[100px] rounded-full" />
            </div>
          ))}
        </ScrollView>
      </div>
    )
  }

  if (hasError) {
    return (
      <div className="col-span-1 -ml-3 flex w-[calc(100%+24px)] flex-col gap-5 md:col-span-2 md:ml-0 md:w-full md:gap-12">
        <div className="px-3 md:px-0">{title}</div>
        <div className="flex items-center gap-3 px-3 text-14 text-zinc-500 md:px-0">
          <span>Couldn&apos;t load recommendations.</span>
          <button
            type="button"
            onClick={() => setRetryToken((token) => token + 1)}
            className="font-medium text-white underline"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (categoriesWithApps.length === 0) return null

  return (
    <div className="col-span-1 -ml-3 flex w-[calc(100%+24px)] flex-col gap-5 md:col-span-2 md:ml-0 md:w-full md:gap-12">
      <div className="px-3 md:px-0">{title}</div>
      <ScrollView className="flex gap-8 px-5 md:px-0" theme="dark">
        {categoriesWithApps.map((category, i) => {
          const apps = category.apps
          const totalApps = apps.length
          const totalDeals = apps.reduce((acc, app) => {
            const appDeals = (app.deals as { docs?: Deal[] } | undefined)?.docs ?? []
            return acc + determineDealsToDisplay(appDeals).length
          }, 0)

          const [app] = apps
          const appIcon = app.icon as Media | null
          const appGlyph = app.glyph as Media | null

          const { backgroundColor } = getBrandColors(app)

          return (
            <Link
              href={`/best/${category.slug}`}
              key={i}
              prefetch={PREFETCH}
              className="group flex cursor-pointer flex-col items-center gap-3 text-center"
            >
              <AppIcon
                icon={appIcon}
                glyph={appGlyph}
                name={app.name ?? category.name}
                background={backgroundColor}
                size={144}
                circular
                className="border border-white/20 group-hover:scale-95"
              />
              <div className="flex flex-col gap-1">
                <div className="leading-tight text-white">{category.name}</div>
                <p className="text-12 text-zinc-500 [&>span]:text-zinc-600">
                  {totalApps} app{totalApps === 1 ? '' : 's'}
                  {totalDeals ? (
                    <span>
                      , {totalDeals} deal{totalDeals === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </p>
              </div>
            </Link>
          )
        })}
      </ScrollView>
    </div>
  )
}
